import { v4 as uuidv4 } from "uuid";
import {
  Exercise,
  ExerciseCollection,
  ExerciseSet,
  Workout,
  WorkoutSection,
} from "@/components/workouts/workoutsColumns";
import { exercisesToExerciseCollection } from "@/lib/utils/WorkoutUtils";
import {
  isLooseSection,
  isWorkoutSectionType,
  soleDefaultSectionIndex,
} from "@trainrr/api/workout-sections";

export {
  WORKOUT_SECTION_TYPES,
  WORKOUT_SECTION_LABELS,
  isLooseSection,
  isWorkoutSectionType,
} from "@trainrr/api/workout-sections";

export const TRAILING_LOOSE_SECTION_ID = "__trailing__";

export const ensureTrailingLooseSection = (workout: Workout) => {
  const ensured = ensureWorkoutSections(workout);
  const last = ensured.sections[ensured.sections.length - 1];
  if (last && isLooseSection(last)) {
    return { workout: ensured, sectionId: last.id };
  }
  const section = createDefaultSection({
    is_default: false,
    position: ensured.sections.length + 1,
  });
  return {
    workout: {
      ...ensured,
      sections: [...ensured.sections, section],
    },
    sectionId: section.id,
  };
};

export const createDefaultSection = (
  overrides: Partial<WorkoutSection> = {},
): WorkoutSection => ({
  id: uuidv4(),
  position: 1,
  name: null,
  section_type: "standard",
  is_default: true,
  rounds: null,
  time_cap_seconds: null,
  interval_seconds: null,
  work_seconds: null,
  rest_seconds: null,
  score_type: null,
  notes: null,
  ...overrides,
});

export const createEmptyEditorWorkout = (
  overrides: Partial<Workout> = {},
): Workout =>
  ensureWorkoutSections({
    id: uuidv4(),
    name: "New workout",
    description: null,
    created_at: "",
    day: null,
    exerciseCollection: [],
    sections: [],
    ...overrides,
  });

export const ensureWorkoutSections = (workout: Workout): Workout => {
  if (workout.sections && workout.sections.length > 0) {
    return workout;
  }
  const section = createDefaultSection({
    is_default: true,
    position: 1,
  });
  return {
    ...workout,
    sections: [section],
    exerciseCollection: workout.exerciseCollection.map((group) =>
      group.map((exercise) => ({
        ...exercise,
        section_id: exercise.section_id ?? section.id,
      })),
    ),
  };
};

type ApiSection = {
  id?: string | null;
  position?: number | null;
  name?: string | null;
  section_type?: string | null;
  is_default?: boolean | null;
  rounds?: number | null;
  time_cap_seconds?: number | null;
  interval_seconds?: number | null;
  work_seconds?: number | null;
  rest_seconds?: number | null;
  score_type?: string | null;
  notes?: string | null;
  exercises?: unknown[] | null;
};

type ApiWorkout = {
  id: string;
  name: string;
  description?: string | null;
  created_at?: string | null;
  day?: number | null;
  exercises?: unknown[] | null;
  sections?: ApiSection[] | null;
};

const toEditorSection = (
  section: ApiSection,
  index: number,
): WorkoutSection => ({
  id: section.id && section.id.length > 0 ? section.id : uuidv4(),
  position: section.position ?? index + 1,
  name: section.name ?? null,
  section_type: isWorkoutSectionType(section.section_type)
    ? section.section_type
    : "standard",
  is_default: Boolean(section.is_default) || index === 0,
  rounds: section.rounds ?? null,
  time_cap_seconds: section.time_cap_seconds ?? null,
  interval_seconds: section.interval_seconds ?? null,
  work_seconds: section.work_seconds ?? null,
  rest_seconds: section.rest_seconds ?? null,
  score_type: section.score_type ?? null,
  notes: section.notes ?? null,
});

export const apiWorkoutToEditor = (data: ApiWorkout): Workout => {
  const sections = (data.sections ?? []).map(toEditorSection);
  const nestedExercises = sections.flatMap((section, index) =>
    ((data.sections?.[index]?.exercises as unknown[]) ?? []).map(
      (exercise) => ({
        ...(exercise as Exercise),
        section_id: (exercise as Exercise).section_id ?? section.id,
      }),
    ),
  );
  const flattenedExercises =
    nestedExercises.length > 0
      ? nestedExercises
      : ((data.exercises ?? []) as Exercise[]);

  const workout = ensureWorkoutSections({
    id: data.id,
    name: data.name,
    description: data.description ?? null,
    created_at: data.created_at ?? "",
    day: data.day ?? null,
    exerciseCollection: exercisesToExerciseCollection(flattenedExercises),
    sections,
  });

  return {
    ...workout,
    exerciseCollection: workout.exerciseCollection.map((group) =>
      group.map((exercise) => ({
        ...exercise,
        section_id:
          exercise.section_id ?? sectionIdForGroup(workout, group) ?? null,
      })),
    ),
  };
};

export const cloneEditorWorkout = (
  sourceWorkout: Workout,
  overrides: Partial<Workout> = {},
): Workout => {
  const ensured = ensureWorkoutSections(sourceWorkout);
  const sectionIdMap = new Map(
    ensured.sections.map((section) => [section.id, uuidv4()]),
  );
  return {
    ...ensured,
    id: uuidv4(),
    ...overrides,
    sections: ensured.sections.map((section) => ({
      ...section,
      id: sectionIdMap.get(section.id) ?? uuidv4(),
    })),
    exerciseCollection: ensured.exerciseCollection.map((group, groupIndex) =>
      group.map((exercise, exerciseIndex) => ({
        ...exercise,
        workout_exercise_id: null,
        ex_order: groupIndex,
        sub_order: exerciseIndex,
        sortableId: uuidv4(),
        section_id:
          sectionIdMap.get(
            exercise.section_id ??
              sectionIdForGroup(ensured, group) ??
              ensured.sections[0].id,
          ) ??
          sectionIdMap.get(ensured.sections[0].id) ??
          ensured.sections[0].id,
        sets: (exercise.sets ?? []).map((set) => ({
          ...set,
          id: uuidv4(),
        })),
      })),
    ),
  };
};

export const sectionIdForGroup = (
  workout: Workout,
  group: ExerciseCollection,
): string | undefined => {
  const explicit = group[0]?.section_id?.trim();
  if (explicit) return explicit;
  return [...workout.sections].reverse().find(isLooseSection)?.id;
};

export const groupsForSection = (workout: Workout, sectionId: string) => {
  const groups: { group: ExerciseCollection; globalIndex: number }[] = [];
  workout.exerciseCollection.forEach((group, globalIndex) => {
    if (sectionIdForGroup(workout, group) === sectionId) {
      groups.push({ group, globalIndex });
    }
  });
  return groups;
};

export const firstGroupIndexForSection = (
  workout: Workout,
  sectionId: string,
) => {
  const found = workout.exerciseCollection.findIndex(
    (group) => sectionIdForGroup(workout, group) === sectionId,
  );
  if (found >= 0) return found;
  let index = 0;
  for (const section of workout.sections) {
    if (section.id === sectionId) return index;
    index += groupsForSection(workout, section.id).length;
  }
  return workout.exerciseCollection.length;
};

export const insertIndexForSection = (
  workout: Workout,
  sectionId: string,
  localIndex: number,
) => firstGroupIndexForSection(workout, sectionId) + localIndex;

const toNullableNumber = (value: unknown): number | null => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return null;
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const toSaveSets = (sets: ExerciseSet[]) =>
  sets.map((set, index) => ({
    id: set.id,
    startRange: set.startRange,
    endRange: set.endRange,
    targetWeight: set.targetWeight,
    targetTimeSeconds: set.targetTimeSeconds,
    targetDistanceMeters: set.targetDistanceMeters,
    set_type: set.set_type ?? "working",
    set_number: index + 1,
    rir: set.rir,
    rpe: set.rpe,
    notes: set.notes,
    rest_seconds: set.rest_seconds,
    tempo: set.tempo,
  }));

export const editorWorkoutToSavePayload = (
  workout: Workout,
  extras: {
    orgId?: string;
    name?: string;
    description?: string;
    isTemplate?: boolean;
  } = {},
) => {
  const ensured = ensureWorkoutSections(workout);
  const defaultSectionIndex = soleDefaultSectionIndex(ensured.sections);
  const sections = ensured.sections.map((section, sectionIndex) => {
    const groups = groupsForSection(ensured, section.id);
    const exercises = groups.flatMap(({ group, globalIndex }) =>
      group.map((exercise, subOrder) => ({
        // `id` is the library exercise id. Older plan RPCs only read this
        // key, so mixed web/API deploys still persist the tree.
        id: exercise.id,
        exercise_id: exercise.id,
        workout_exercise_id: exercise.workout_exercise_id ?? undefined,
        sets: toSaveSets(exercise.sets ?? []),
        tempo: exercise.tempo ?? "",
        rir: toNullableNumber(exercise.rir),
        rest_time: toNullableNumber(exercise.rest_time),
        coach_note: exercise.coach_note ?? null,
        // PK is (workout_id, ex_order, sub_order) — must be unique across
        // the whole workout, not restarted per section.
        ex_order: globalIndex,
        sub_order: subOrder,
        position: globalIndex,
        superset_key: group.length > 1 ? `S${globalIndex + 1}` : null,
        superset_position: subOrder,
        tracking_type_override: exercise.tracking_type_override ?? null,
      })),
    );

    return {
      id: section.id,
      position: sectionIndex + 1,
      name: section.name,
      section_type: section.section_type,
      is_default: sectionIndex === defaultSectionIndex,
      rounds: section.rounds,
      time_cap_seconds: section.time_cap_seconds,
      interval_seconds: section.interval_seconds,
      work_seconds: section.work_seconds,
      rest_seconds: section.rest_seconds,
      score_type: null,
      notes: section.notes,
      exercises,
    };
  });

  return {
    workout: {
      id: ensured.id,
      org_id: extras.orgId,
      name: extras.name ?? ensured.name,
      description: extras.description ?? ensured.description ?? "",
      is_template: extras.isTemplate,
      sections,
      // Older plan-save RPCs only read top-level exercises[]. Keep sending
      // a flatten so mixed-version deploys do not drop the workout body.
      exercises: sections.flatMap((section) => section.exercises),
    },
  };
};

export const stampSectionId = (
  group: ExerciseCollection,
  sectionId: string,
): ExerciseCollection =>
  group.map((exercise) => ({ ...exercise, section_id: sectionId }));
